// Plain-English explanations for System Logs lines.
// Patterns are based on the real messages in ops_logs: the NinjaTrader bridge log (source "broker",
// forwarded by the uplink on Eli's laptop), the uplink's own notes (source "system", "UPLINK: ...",
// "Recovery lock: ...", "Sim101 orders switch: ...") and the dashboard's own lines (watchdog,
// emergency stop, data purge, API key rotation).
//
// explainLog(log) -> { title, meaning, action, tone }   tone: 'info' | 'good' | 'warning' | 'problem'

const NOTHING = 'Nothing to do.';
const TELL_TAB_IF_REPEATS = 'Nothing to do now. If it keeps repeating, tell Tab.';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const num = (s) => { const n = parseFloat(String(s).replace(/,/g, '')); return Number.isFinite(n) ? n : null; };
export const fmtPrice = (n) => (n == null ? '?' : n.toLocaleString('en-US', { maximumFractionDigits: 2 }));
const fmtPoints = (n) => `${fmtPrice(Math.round(n * 100) / 100)} ${Math.abs(n) === 1 ? 'point' : 'points'}`;
const contracts = (q) => `${q} ${Number(q) === 1 ? 'contract' : 'contracts'}`;

// "MNQ 12-26" -> "MNQ (December 2026)"
const instrument = (s) => {
  const m = /^\s*([A-Z0-9]+)\s+(\d{1,2})-(\d{2})\s*$/.exec(s || '');
  if (!m) return (s || '').trim() || 'the contract';
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${m[1]} (${month} 20${m[3]})` : m[1];
};

// NinjaTrader order actions -> what it means for the trade.
const isBuyAction = (a) => /^buy/i.test(a || ''); // Buy or BuyToCover
const entryWords = (a) => {
  const u = (a || '').toUpperCase().replace(/_/g, '');
  if (u === 'BUY') return 'bought';
  if (u === 'SELLSHORT' || u === 'SELL') return 'sold short (a bet that the price falls)';
  return a;
};

// "62 < 70", "score=62 min=70", "score 62 below 70" ... -> "score 62 is below the 70 needed"
export const translateReason = (raw) => {
  const r = (raw || '').trim().replace(/^[:\-–—\s]+|[\s.]+$/g, '');
  if (!r) return '';
  const score = /score\D{0,12}?(\d+(?:\.\d+)?)\D{0,24}?(?:<|below|under|min(?:imum)?|need(?:ed|s)?|threshold|required)\D{0,12}?(\d+(?:\.\d+)?)/i.exec(r);
  if (score) return `the setup's score ${score[1]} is below the ${score[2]} needed`;
  if (/daily loss/i.test(r)) return 'the daily loss limit has been reached';
  if (/recovery|lock/i.test(r)) return 'the recovery lock is on (a trade is open or still being checked)';
  if (/position|already|open trade/i.test(r)) return 'a trade is already open';
  if (/session|hours|closed|outside/i.test(r)) return 'it is outside the hours the bot is allowed to trade';
  if (/cooldown|cool-down/i.test(r)) return 'the bot is in a short pause after its last trade';
  if (/spread|volatil|atr/i.test(r)) return `market conditions were not right (${r})`;
  if (/disabled|ENABLE_SIM101/i.test(r)) return 'the trading switch is off';
  return r;
};

const RULES = [
  // ---- Bracket (one trade's life cycle in the NinjaTrader bridge) ----
  {
    re: /connection event ignored \(account still Connected\):\s*(.*)$/i,
    explain: (m) => ({
      title: 'Connection flicker, ignored',
      meaning: `NinjaTrader's simulated connection flickered (${m[1].trim()}) but the account stayed connected, so the bot carried on as normal.`,
      action: NOTHING, tone: 'info',
    }),
  },
  {
    re: /ENTRY SUBMITTED\s+(\w+)\s+(\d+)\s+(.+?)\s+stop=([\d.,]+)\s+target=([\d.,]+)/i,
    explain: (m) => {
      const stop = num(m[4]); const target = num(m[5]);
      return {
        title: 'Trade opened',
        meaning: `The bot ${entryWords(m[1])} ${contracts(m[2])} of ${instrument(m[3])} on the practice account. `
          + `It comes with a protective stop at ${fmtPrice(stop)} and a profit target at ${fmtPrice(target)} `
          + `(${fmtPoints(Math.abs(target - stop))} between them). Whichever is reached first closes the trade.`,
        action: NOTHING, tone: 'info',
      };
    },
  },
  {
    re: /STOP SUBMITTED\s+(\w+)\s+(\d+)\s*@\s*([\d.,]+)(?:\s+fill=([\d.,]+))?/i,
    explain: (m) => {
      const stop = num(m[3]); const fill = m[4] != null ? num(m[4]) : null;
      const long = !isBuyAction(m[1]); // a Sell stop protects a bought position; BuyToCover protects a short one
      const pos = fill != null ? ` for the position ${long ? 'bought' : 'sold short'} at ${fmtPrice(fill)}` : '';
      const risk = fill != null && stop != null ? ` (${fmtPoints(Math.abs(fill - stop))} of risk)` : '';
      return {
        title: 'Protective stop placed',
        meaning: `Protective stop placed at ${fmtPrice(stop)}${pos}${risk}. If the price moves against the trade and reaches it, the trade closes automatically to limit the loss.`,
        action: NOTHING, tone: 'good',
      };
    },
  },
  {
    re: /TARGET SUBMITTED\s+(\w+)\s+(\d+)\s*@\s*([\d.,]+)/i,
    explain: (m) => ({
      title: 'Profit target placed',
      meaning: `Profit target placed at ${fmtPrice(num(m[3]))}. If the price reaches it, the trade closes automatically with a profit.`,
      action: NOTHING, tone: 'good',
    }),
  },
  {
    re: /RETIRED,\s*next trade allowed:\s*(.*)$/i,
    explain: (m) => {
      const why = m[1].trim();
      if (/stop filled/i.test(why)) return { title: 'Trade closed at its stop', meaning: 'The trade closed at its stop loss. The bot is free to take the next trade.', action: NOTHING, tone: 'info' };
      if (/target filled/i.test(why)) return { title: 'Trade closed at its target', meaning: 'The trade reached its profit target and closed. The bot is free to take the next trade.', action: NOTHING, tone: 'good' };
      return { title: 'Trade finished', meaning: `The trade is finished (${why}). The bot is free to take the next trade.`, action: NOTHING, tone: 'info' };
    },
  },
  {
    re: /ORDER SUBMITTED:.*?account=(\S+).*?instrument=(.+?)\s+action=(\S+)\s+type=(\S+)\s+qty=(\d+)(?:.*?bracket_stop=([\d.,]+))?(?:.*?bracket_target=([\d.,]+))?/i,
    explain: (m) => {
      const how = /market/i.test(m[4]) ? 'at the current market price' : `as a ${m[4].toLowerCase()} order`;
      const side = /short/i.test(m[3]) ? 'a sell-short order (a bet that the price falls)' : /buy/i.test(m[3]) ? 'a buy order' : `a ${m[3].toLowerCase()} order`;
      const bracket = m[6] && m[7] ? ` It asked for a stop at ${fmtPrice(num(m[6]))} and a profit target at ${fmtPrice(num(m[7]))}.` : '';
      return {
        title: 'Order sent to NinjaTrader',
        meaning: `The bot sent ${side} for ${contracts(m[5])} of ${instrument(m[2])} ${how} on the ${m[1]} practice account.${bracket}`,
        action: NOTHING, tone: 'info',
      };
    },
  },

  // ---- Trade decisions ----
  {
    re: /TRADE GATE\b.*?\bBLOCKED\b\s*[:\-–—]?\s*(.*)$/i,
    explain: (m) => {
      const reason = translateReason(m[1].replace(/^reason\s*[=:]\s*/i, ''));
      return {
        title: 'Setup skipped',
        meaning: `The bot looked at a setup but decided not to trade.${reason ? ` Reason: ${reason}.` : ''}`,
        action: 'Nothing to do. Skipping weak setups is the bot doing its job.', tone: 'info',
      };
    },
  },
  {
    re: /TRADE GATE\b.*?\b(PASS(?:ED)?|OPEN|ALLOWED|OK)\b/i,
    explain: () => ({ title: 'Setup approved', meaning: 'A setup passed all of the bot\'s checks, so it is allowed to trade it.', action: NOTHING, tone: 'good' }),
  },
  {
    re: /TRADE GATE\b/i,
    explain: () => ({ title: 'Trade check', meaning: 'The bot ran its checks on a possible trade.', action: NOTHING, tone: 'info' }),
  },
  {
    re: /PROTECTED TRADE\b/i,
    explain: () => ({ title: 'Trade protected', meaning: 'The open trade has its protective stop and profit target in place.', action: NOTHING, tone: 'good' }),
  },
  {
    re: /MARGIN SIZING\b/i,
    explain: () => ({ title: 'Trade size check', meaning: 'The bot worked out how many contracts it can trade with the money available in the account.', action: NOTHING, tone: 'info' }),
  },
  {
    re: /DAILY LOSS\b/i,
    explain: (m, msg) => (/hit|reached|exceed|breach|halt|stop/i.test(msg)
      ? { title: 'Daily loss limit reached', meaning: 'The bot has lost as much as it is allowed to in one day, so it stops taking new trades for the rest of the day.', action: 'Nothing to do. It starts again next session. Tell Tab if it happens often.', tone: 'warning' }
      : { title: 'Daily loss check', meaning: 'A note about the bot\'s daily loss limit (the most it is allowed to lose in one day).', action: NOTHING, tone: 'info' }),
  },

  // ---- Safety switches ----
  {
    re: /^Recovery lock:\s*PRESENT/i,
    explain: () => ({
      title: 'One trade at a time',
      meaning: 'A trade is open, so the bot will not open a second one until this one closes. Normal.',
      action: 'Nothing to do. If this stays on while no trade is open (Positions shows flat), tell Tab.', tone: 'info',
    }),
  },
  {
    re: /^Recovery lock:\s*clear/i,
    explain: () => ({ title: 'Ready for the next trade', meaning: 'The lock is off: the bot is free to open a new trade when a good setup appears.', action: NOTHING, tone: 'good' }),
  },
  {
    re: /RECOVERY_REQUIRED\.lock exists/i,
    explain: () => ({
      title: 'Bot is waiting for a person',
      meaning: 'When NinjaTrader started, a safety lock from earlier was still there. The bot will not open any new trades until someone checks the practice account and removes the lock.',
      action: 'Tell Tab or Eli: they need to check the Sim101 account in NinjaTrader and clear the lock.', tone: 'warning',
    }),
  },
  {
    re: /(?:Sim101 orders switch:\s*ON|ENABLE_SIM101_ORDERS\.txt is PRESENT)/i,
    explain: () => ({ title: 'Trading switch is on', meaning: 'The bot is allowed to place trades on the practice account.', action: NOTHING, tone: 'good' }),
  },
  {
    re: /(?:Sim101 orders switch:\s*OFF|ENABLE_SIM101_ORDERS\.txt is MISSING)/i,
    explain: () => ({ title: 'Trading switch is off', meaning: 'The bot is watching the market but is not allowed to place any trades.', action: 'If the bot should be trading, tell Tab.', tone: 'warning' }),
  },
  {
    re: /EXECUTION HALTED/i,
    explain: () => ({ title: 'Bot stopped trading', meaning: 'The bot stopped placing trades because something went wrong.', action: 'Check NinjaTrader on Eli\'s laptop and tell Tab.', tone: 'problem' }),
  },
  {
    re: /ProtectionError/i,
    explain: () => ({
      title: 'Problem protecting a trade',
      meaning: 'The bot could not confirm the protective stop or profit target for a trade.',
      action: 'Check right away in NinjaTrader that any open trade has a stop, and tell Tab.', tone: 'problem',
    }),
  },
  {
    re: /RECONCILIATION/i,
    explain: (m, msg) => (/mismatch|fail|differ|error/i.test(msg)
      ? { title: 'Records do not match', meaning: 'The bot compared its own records with the NinjaTrader account and found a difference.', action: 'Tell Tab.', tone: 'warning' }
      : { title: 'Records check', meaning: 'The bot compared its own records with the NinjaTrader account to make sure they match.', action: NOTHING, tone: 'info' }),
  },

  // ---- Bridge start-up and housekeeping ----
  {
    re: /^STARTUP:.*Bridge build=/i,
    explain: () => ({ title: 'Bridge started', meaning: 'The bot\'s link inside NinjaTrader (the bridge) just started up, usually because NinjaTrader was opened or restarted.', action: NOTHING, tone: 'info' }),
  },
  {
    re: /^WORKER:\s*started/i,
    explain: () => ({ title: 'Ready for instructions', meaning: 'The part of the bridge that carries out the bot\'s orders has started and is ready.', action: NOTHING, tone: 'good' }),
  },
  {
    re: /^STALE PURGE:\s*archived (\d+)/i,
    explain: (m) => ({
      title: 'Old instructions cleared',
      meaning: Number(m[1]) > 0
        ? `On start-up the bridge set aside ${m[1]} old instruction${m[1] === '1' ? '' : 's'} without carrying ${m[1] === '1' ? 'it' : 'them'} out, so nothing old can fire late by accident. A safety feature.`
        : 'On start-up the bridge checked for old leftover instructions so nothing old can fire late by accident. There was nothing to clear.',
      action: NOTHING, tone: 'info',
    }),
  },
  {
    re: /^STALE COMMAND\b/i,
    explain: () => ({
      title: 'Old instruction skipped',
      meaning: 'An instruction written before NinjaTrader restarted was set aside without being carried out. A safety feature, so old orders never fire late.',
      action: NOTHING, tone: 'info',
    }),
  },
  {
    re: /MOVE EXCEPTION:.*FileNotFound/i,
    explain: () => ({
      title: 'Tidy-up hiccup',
      meaning: 'The bridge tried to file away an old instruction that was already gone. Harmless on its own.',
      action: TELL_TAB_IF_REPEATS, tone: 'warning',
    }),
  },
  {
    re: /^(?:at\s+[\w.<>`]+\(|File name:\s*')/,
    explain: () => ({ title: 'Technical detail', meaning: 'Part of the technical details of the error line just before this one. See that line\'s explanation instead.', action: NOTHING, tone: 'info' }),
  },

  // ---- Uplink (the program on Eli's laptop that feeds this dashboard) ----
  {
    re: /^UPLINK:\s*connected to NinjaTrader bridge/i,
    explain: () => ({ title: 'Dashboard link connected', meaning: 'The program on Eli\'s laptop that feeds this dashboard is talking to NinjaTrader. Live data is flowing.', action: NOTHING, tone: 'good' }),
  },
  {
    re: /^UPLINK:\s*NinjaTrader bridge not answering/i,
    explain: () => ({
      title: 'NinjaTrader not answering',
      meaning: 'The program on Eli\'s laptop that feeds this dashboard could not reach NinjaTrader. Usually NinjaTrader is closed, restarting or busy. Dashboard numbers stay frozen until it answers again.',
      action: 'If this keeps appearing for more than a few minutes during trading hours, check that NinjaTrader is open on Eli\'s laptop.', tone: 'warning',
    }),
  },
  {
    re: /^UPLINK:\s*\S+ started/i,
    explain: () => ({ title: 'Dashboard link started', meaning: 'The program on Eli\'s laptop that feeds this dashboard just started. It only reads from NinjaTrader and never places trades itself.', action: NOTHING, tone: 'info' }),
  },
  {
    re: /^UPLINK:\s*dashboard EMERGENCY STOP is set/i,
    explain: () => ({
      title: 'Emergency stop needs a person',
      meaning: 'Emergency stop is switched on here, but the link on Eli\'s laptop can only read, so it cannot close trades by itself.',
      action: 'Close any open trade by hand in NinjaTrader (Flatten).', tone: 'problem',
    }),
  },

  // ---- Dashboard's own lines ----
  {
    re: /^WATCHDOG:.*heartbeat lost.*?(\d+)s ago/i,
    explain: (m) => {
      const s = Number(m[1]);
      const ago = s >= 120 ? `about ${Math.round(s / 60)} minutes` : `${s} seconds`;
      return {
        title: 'Bot went quiet',
        meaning: `The dashboard has not heard from the bot for ${ago}. Eli's laptop, NinjaTrader or the internet connection may be down.`,
        action: 'Check that Eli\'s laptop is on and NinjaTrader is open. If it does not come back, tell Tab.', tone: 'problem',
      };
    },
  },
  {
    re: /^EMERGENCY STOP by /i,
    explain: () => ({ title: 'Emergency stop pressed', meaning: 'Someone pressed Emergency Stop: the bot was told to close all open trades, cancel all orders and stop.', action: 'Check that the bot confirms it is flat (a later line says so).', tone: 'problem' }),
  },
  {
    re: /^Emergency stop lifted/i,
    explain: () => ({ title: 'Emergency stop lifted', meaning: 'The emergency stop was switched off, so the bot is allowed to trade again.', action: NOTHING, tone: 'good' }),
  },
  {
    re: /^Bot confirmed FLAT/i,
    explain: () => ({ title: 'Bot is flat', meaning: 'After the emergency stop the bot confirmed it has no open trades and no orders waiting.', action: NOTHING, tone: 'good' }),
  },
  {
    re: /^All dashboard data purged/i,
    explain: () => ({ title: 'Dashboard data cleared', meaning: 'An admin wiped the dashboard\'s stored data to start fresh. The bot itself was not affected.', action: NOTHING, tone: 'warning' }),
  },
  {
    re: /^API key rotated/i,
    explain: () => ({ title: 'Connection key changed', meaning: 'An admin replaced the secret key the bot uses to send data to this dashboard.', action: 'The program on Eli\'s laptop must be given the new key, otherwise the dashboard stops getting data. Tell Tab if data stops.', tone: 'warning' }),
  },

  // ---- Broad keyword fallbacks ----
  {
    re: /\bheartbeat\b/i,
    explain: () => ({ title: 'Heartbeat', meaning: 'A regular "I\'m alive" signal between the bot and the dashboard.', action: NOTHING, tone: 'info' }),
  },
  {
    re: /\bdisconnected\b/i,
    explain: () => ({ title: 'Connection dropped', meaning: 'A connection dropped (for example NinjaTrader to its data, or the bot to the dashboard).', action: TELL_TAB_IF_REPEATS, tone: 'warning' }),
  },
  {
    re: /\bBLOCKED\b|\bREFUSED\b/i,
    explain: () => ({ title: 'Action held back', meaning: 'The bot held back from doing something because one of its safety checks said no.', action: TELL_TAB_IF_REPEATS, tone: 'info' }),
  },
  {
    re: /\bREJECTED\b/i,
    explain: () => ({ title: 'Something was refused', meaning: 'An order or request was refused (for example by NinjaTrader).', action: TELL_TAB_IF_REPEATS, tone: 'warning' }),
  },
  {
    re: /\bstale\b/i,
    explain: () => ({ title: 'Out-of-date information', meaning: 'Some information was old, so the bot did not use it.', action: TELL_TAB_IF_REPEATS, tone: 'info' }),
  },
];

const FALLBACK = {
  critical: { title: 'Serious error', meaning: 'Something important failed.', action: 'Tell Tab now.', tone: 'problem' },
  error: { title: 'Error', meaning: 'An error: something failed.', action: 'If it repeats, tell Tab.', tone: 'problem' },
  warning: { title: 'Warning', meaning: 'A warning: something unusual happened but the bot kept running.', action: TELL_TAB_IF_REPEATS, tone: 'warning' },
  info: { title: 'Note', meaning: 'An informational note from the trading bot.', action: NOTHING, tone: 'info' },
};

const SOURCE_WORDS = {
  broker: 'the NinjaTrader bridge (the bot\'s link inside NinjaTrader)',
  system: 'the system',
  watchdog: 'the dashboard\'s watchdog',
  engine: 'the trading bot',
  risk: 'the risk checks',
  data: 'the market data feed',
};

export function explainLog(log) {
  const raw = String(log?.message ?? '');
  // "BRACKET <name>: ..." prefixes every bracket line; the rules look at what follows.
  const msg = raw.replace(/^BRACKET\s+\S+:\s*/i, '').trim();
  for (const rule of RULES) {
    const m = rule.re.exec(msg);
    if (m) return rule.explain(m, msg);
  }
  const sev = FALLBACK[(log?.severity || 'info').toLowerCase()] || FALLBACK.info;
  const from = SOURCE_WORDS[(log?.source || '').toLowerCase()];
  if (!from) return { ...sev };
  const meaning = sev === FALLBACK.info
    ? `An informational note from ${from}.`
    : `${sev.meaning.replace(/\.$/, '')} (in ${from}).`;
  return { ...sev, meaning };
}

export default explainLog;
